import React, { useCallback, useState, useMemo } from 'react';
import { useField } from 'formik';
import { useTranslation } from '@blocs.i18n';
import { BasicContainer, BodyText, ButtonAsWrapper } from '@minecraft.atoms';
import { FieldWrapper, FieldWrapperProps } from './FieldWrapper';
import { BaseInput, BaseInputProps, getHelpTextValue, normalizeFormikError } from './BaseInput';

export type BasePasswordInputProps = BaseInputProps & {
  /** text to display when the password is shown */
  showText?: string;
  /** text to display when the password is hidden */
  hideText?: string;
};

export const BasePasswordInput = ({ disabled = false, showText, hideText, ...inputProps }: BasePasswordInputProps) => {
  const { t } = useTranslation();
  const [isShow, setIsShow] = useState(false);

  const handleShowHide = useCallback(
    (e: React.MouseEvent<HTMLButtonElement>) => {
      e.preventDefault();
      e.stopPropagation();
      setIsShow(!isShow);
    },
    [isShow]
  );

  const { inputType, toggleLabel } = useMemo(() => {
    // Only use showText/hideText if they are valid strings
    const intlHideText = typeof hideText === 'string' ? hideText : t('common:button.hide');
    const intlShowText = typeof showText === 'string' ? showText : t('common:button.show');

    return {
      inputType: isShow ? ('text' as const) : ('password' as const),
      toggleLabel: isShow ? intlHideText : intlShowText,
    };
  }, [isShow, hideText, showText, t]);

  return (
    <BasicContainer className="cn_relative" w="full">
      <BaseInput {...inputProps} type={inputType} disabled={disabled} />
      <BasicContainer className="cn_absolute" top={0.5} right={0.5}>
        <ButtonAsWrapper onClick={handleShowHide} disabled={disabled} aria-label={toggleLabel}>
          <BodyText color="statusDefault" fontWeight="semibold">
            {toggleLabel}
          </BodyText>
        </ButtonAsWrapper>
      </BasicContainer>
    </BasicContainer>
  );
};

export type ConnectedPasswordInputProps = {
  /** name of the field in the formik state */
  name: string;
  /** is the field disabled */
  disabled?: boolean;
  /** label for the input */
  label?: string;
  /** help text to display below the input */
  helpText?: string;
  /** props passed directly through to the underlying input, you should not need this in most cases */
  inputProps?: Omit<
    BasePasswordInputProps,
    'invalid' | 'onChange' | 'onBlur' | 'value' | 'type' | 'id' | 'name' | 'min' | 'max'
  >;
  /** props passed directly through to the <FieldWrapper /> component, you should not need this in most cases */
  wrapperProps?: Omit<
    FieldWrapperProps,
    'id' | 'label' | 'helpText' | 'disabled' | 'validation' | 'tooltip' | 'helpTextAbove'
  >;
  /** tooltip to display next to the label */
  tooltip?: React.ReactNode;
  /** help text to display above the input */
  helpTextAbove?: string;
  /** prevents the user from entering a value longer than maxLength
   * also sets helpText below the input if maxLength is reached to `${maxLength} Character limit reached` */
  maxLength?: number;
};

export const PasswordInput = ({
  name,
  disabled = false,
  label,
  helpText: helpTextProp,
  wrapperProps,
  inputProps,
  tooltip,
  helpTextAbove,
  maxLength,
}: ConnectedPasswordInputProps) => {
  const { t } = useTranslation();
  const [input, meta, helpers] = useField<string>(name);
  const { value: formikValue, onBlur } = input;
  const { error } = meta;
  const hasMaxLength = typeof maxLength === 'number';
  const helpText = getHelpTextValue({ maxLength, helpTextProp, currentValue: formikValue, t });

  const hasError = !!error;
  const fieldValidation = hasError ? { invalid: true } : {};
  const wrapperValidation = hasError ? { validation: { valid: !hasError, message: normalizeFormikError(error) } } : {};

  const onChange = (newVal: string) => {
    let valToSet = newVal;
    if (hasMaxLength && newVal?.length >= maxLength) {
      valToSet = newVal.slice(0, maxLength);
    }
    helpers.setValue(valToSet);
  };

  // this prevents the console warning about switching between
  // controlled and uncontrolled input types
  let inputValue = formikValue;

  if (inputValue === undefined || inputValue === null) {
    inputValue = '';
  }

  return (
    <FieldWrapper
      label={label}
      helpText={helpText}
      {...wrapperProps}
      {...wrapperValidation}
      id={name}
      disabled={disabled}
      tooltip={tooltip}
      helpTextAbove={helpTextAbove}
    >
      <BasePasswordInput
        {...inputProps}
        {...fieldValidation}
        value={inputValue}
        name={name}
        onChange={onChange}
        onBlur={onBlur}
        disabled={disabled}
      />
    </FieldWrapper>
  );
};
